import {
  useCurrentDate,
  Anchor,
  Button,
  InputCheckbox,
  InputNumber,
} from "@sellar-io/components";
import {
  BusinessGetResponseType,
  CompanyCreditInfoResponseType,
  SupplierGetResponseType,
  SupplierRelationshipGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { useMarketStorefrontSessionId } from "../../../../hooks/useMarketStorefrontSessionTracking";
import { CheckoutContextApi } from "./CheckoutProvider.shared";
import { ComponentProps, ReactNode, useCallback, useState } from "react";
import {
  calculateCardPaymentDeposit,
  daysOfWeek,
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
  getNextCourierDispatchAndDeliveryDates,
  getNextStartDate,
} from "@sellar-io/common";
import { CheckoutDialog } from "./CheckoutDialog";
import { CheckoutEditAddressForm } from "./CheckoutEditAddressForm";
import { CheckoutEditPhoneNumberForm } from "./CheckoutEditPhoneNumberForm";
import { CheckoutEditPurchaseOrderForm } from "./CheckoutEditPurchaseOrderForm";
import { mapToDraughtRequirements } from "./mapToDraughtRequirements";
import { CheckoutEditDraughtDutyForm } from "./CheckoutEditDraughtDutyForm";
import {
  Badge,
  BadgeBlue,
  BadgeYellow,
  BadgeOrange,
} from "../../../../components/badge/Badge";
import { Icon } from "../../../../components";
import { ElementsConsumer } from "@stripe/react-stripe-js";
import { twMerge } from "tailwind-merge";
import { SlideTransition } from "../../../../components/transition/SlideTransition";
import {
  Dialog,
  IconType,
  DialogNavigation,
  DialogContent,
} from "@sellar-io/components";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { StripeProvider } from "./StripeProvider";
import {
  CheckoutAddress,
  CheckoutCreditTerms,
  CheckoutOrder,
  CheckoutPaymentType,
} from "./types";
import { useCheckoutOrderSummary } from "./useCheckoutOrderSummary";
import { useIsDesktop } from "./useIsDesktop";
import { Alert } from "../../../../components/alert/Alert";
import { FulfillmentTypeRadioSelectorV2 } from "../../Components/FulfillmentTypeRadioSelectorV2";
import { CheckoutFulfillmentTypeSelector } from "../../Components/CheckoutFulfillmentTypeSelector";
import { useBasketContext, useEvents } from "../../../../hooks";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import sellarLogoNew from "../../../../images/sellar-logo-new.png";
import { getNextDirectDeliveryDate } from "../../../order/helpers/DeliveryDateHelpers/getNextDeliveryDate";
import { useCourierDeliveryScheduleList } from "../../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleList";
import { SellarDistributionContactCard } from "./SellarDistributionContactCard";
import { TwoSellarCreditVerificationRequiredAlert } from "../../../../components/twoSellarCreditVerificationRequiredAlert/TwoSellarCreditVerificationRequiredAlert";
import { PlaceOrderMode, PriceMismatchMode } from "./usePlaceOrder";
import { v4 } from "uuid";
import { useNavigate } from "react-router";
import db from "../../../../db";
import { CheckoutCustomCommentForm } from "./CheckoutCustomCommentForm";
import { CheckoutSelectedFulfillmentType } from "../../StockList";
import { useOpenDistributionOrderList } from "../../../../hooks/api/v2/order/v2/useOpenDistributionOrderList";
import { useDistributionDeliveryEstimate } from "../../../../hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";
import { CardPaymentOptions } from "./CardPaymentOptions";
import { InvoiceSettings, type InvoiceMethodOption } from "./InvoiceSettings";
import {
  PayAtCheckoutPaymentOption,
  SellarCreditContent,
  SellarCreditPaymentOption,
  SupplierCreditPaymentOption,
} from "./PaymentOptions";
import { RyftCardPaymentOption } from "./RyftCardPaymentOption";

type BasePlaceOrderParams = {
  order: CheckoutOrder;
  options: {
    idempotencyKey?: string;
  };
};

type PlaceOrderHandler<
  TParams extends BasePlaceOrderParams = BasePlaceOrderParams,
> = (params: TParams) => void;

type CheckoutProps = {
  title: string;
  business: BusinessGetResponseType;
  supplier: SupplierGetResponseType;
  initialCheckoutOrder: CheckoutOrder;
  isPlacingOrder: boolean;
  hasOutOfStockItems: boolean;
  orderSummary: ReturnType<typeof useCheckoutOrderSummary>;
  basketOrder: ReturnType<typeof useBasketContext>["0"]["order"];
  hasDraughtDutyOrderItems: boolean;
  canUserViewSellarCreditBalance: boolean;
  isOrderValueGreaterThanCreditBalance: boolean;
  isValidatingCart: boolean;
  initialIdempotencyKey?: string;
  checkoutProviderProps: Pick<
    CheckoutContextApi,
    | "connection"
    | "storefront"
    | "paymentMethods"
    | "priceTierLink"
    | "checkoutFulfillmentType"
    | "updateFulfillmentType"
  >;
  sellarCreditBalance: number;
  sellarCreditStatus: CompanyCreditInfoResponseType["status"];
  isCompanyVerified: CompanyCreditInfoResponseType["isCompanyVerified"];
  creditProvider: CompanyCreditInfoResponseType["creditProvider"];
  availablePaymentTypes: CheckoutPaymentType[];

  placeOrderMode: PlaceOrderMode;
  onPlaceOrderWithoutStripe: PlaceOrderHandler;
  onPlaceOrderWithStripe: PlaceOrderHandler<
    BasePlaceOrderParams & {
      stripe: Stripe | null;
      elements: StripeElements | null;
    }
  >;
  onUpdateInvoiceEmails: (emails: string[]) => void;
  onValidateCheckout: () => Promise<void>;
  onPlaceOrderWithRyft: PlaceOrderHandler;
};

type ActiveDisclosure = "delivery" | "details" | "payments";

type ActiveDialog =
  | "deliveryAddress"
  | "phoneNumber"
  | "deliveryInstructions"
  | "purchaseOrder"
  | "invoiceAddress"
  | "draughtDuty"
  | "comment";

type PlaceOrderButtonProps = {
  label?: string;
  className?: string;
  idempotencyKey?: string;
  price?: number;
};

/**Checks if two array of strings are equal. It does not consider the order of items in the array */
function areEqual(arr1: string[], arr2: string[]) {
  if (arr1.length !== arr2.length) return false;
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();
  return sorted1.every((val, i) => val === sorted2[i]);
}

export const Checkout = ({
  isPlacingOrder,
  initialCheckoutOrder,
  supplier,
  business,
  hasOutOfStockItems,
  orderSummary,
  basketOrder,
  hasDraughtDutyOrderItems,
  canUserViewSellarCreditBalance,
  isOrderValueGreaterThanCreditBalance,
  sellarCreditBalance,
  sellarCreditStatus,
  isCompanyVerified,
  creditProvider,
  availablePaymentTypes,
  isValidatingCart,
  checkoutProviderProps: {
    connection,
    storefront,
    paymentMethods,
    priceTierLink,
    checkoutFulfillmentType,
    updateFulfillmentType,
  },
  initialIdempotencyKey,
  placeOrderMode,
  onUpdateInvoiceEmails,
  onPlaceOrderWithStripe,
  onPlaceOrderWithoutStripe,
  onPlaceOrderWithRyft,
  onValidateCheckout,
}: CheckoutProps) => {
  const navigate = useNavigate();
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const { isDesktop } = useIsDesktop();
  const [activeDisclosure, setActiveDisclosure] =
    useState<ActiveDisclosure>("delivery");
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const updatedCheckoutOrder = updatePreselectedPaymentOption({
    initialCheckoutOrder,
    availablePaymentTypes,
    isOrderValueGreaterThanCreditBalance,
  });

  const [order, setOrder] = useState<CheckoutOrder>(updatedCheckoutOrder);
  const [hasAcknowledgedPreorder, setHasAcknowledgedPreorder] = useState(false);
  const [hasAcknowledgedOutOfOffice, setHasAcknowledgedOutOfOffice] =
    useState(false);

  const initialInvoiceEmails = business.invoiceEmails;
  const [invoiceEmails, setInvoiceEmails] = useState(initialInvoiceEmails);

  const supplierName = supplier.Business.name;
  const stripeAccountId = supplier.stripeAccountId ?? undefined;

  const { isActive: isFeatureFlagActive } = useRemoteFeatureFlags();
  const isPaymentLinkRequestEnabled = isFeatureFlagActive(
    "checkoutPaymentLinkRequest",
  );
  const isImplicitProformaEnabled = isFeatureFlagActive("implicitProforma");

  const invoiceMethodOptions = mapToAvailableInvoiceMethods({
    stripeAccountId,
  });
  const latestPreorderDate = findLatestPreOrderAvailableFromDate(
    basketOrder?.OrderItems?.map((orderItem) => ({
      availableFrom: orderItem.Product.availableFrom,
    })) ?? [],
  );
  const productIds =
    basketOrder?.OrderItems?.map((orderItem) => orderItem.ProductId) ?? [];

  const isSupplierOutOfOffice = Boolean(
    supplier.SupplierOutOfOffice?.outOfOffice,
  );
  const buyerPostcode = business.DeliveryAddress.postalCode;
  const outOfOfficeShippingDate = useCalculateShippingDateForOutOfOffice({
    supplier,
    deliveryType: order.deliveryType,
    deliveryArea: storefront?.DeliveryArea ?? null,
    latestAvailablePreOrderFromDate: latestPreorderDate,
    productIds,
    buyerPostcode,
  });
  const canDisplayOutOfOfficeAcknowledgement = Boolean(
    isSupplierOutOfOffice && outOfOfficeShippingDate,
  );
  const hasAcceptedOutOfOfficeTerms = canDisplayOutOfOfficeAcknowledgement
    ? hasAcknowledgedOutOfOffice
    : true;
  const isPreorder = Boolean(latestPreorderDate);
  const hasAcceptedPreorderTerms = isPreorder ? hasAcknowledgedPreorder : true;
  const connectionPaymentDue = connection?.paymentDue ?? 0;
  const connectionPaymentDueCalculateFrom =
    connection?.paymentDueCalculateFrom ?? null;
  const hasInvoiceEmailsChanged = !areEqual(
    initialInvoiceEmails,
    invoiceEmails,
  );
  const hasMailingListPermission = connection?.mailingListPermission ?? false;
  const isUnbundling = useRemoteFeatureFlags(supplier.Business.id).isActive(
    "unbundlingReleaseFinal",
  );

  const isSellarDistribution = checkoutFulfillmentType === "sellarDistribution";

  const hasSelectedCardPayment =
    order.payment.type === "existing-card-payment" ||
    order.payment.type === "new-card-payment";

  const hasSelectedRyftCardPayment =
    order.payment.type === "new-ryft-card-payment";

  const hasSelectedCardPaymentWithDeposit =
    order.payment.type === "new-card-payment-with-deposit" ||
    order.payment.type === "existing-card-payment-with-deposit";

  const canPlaceOrderWithStripe =
    availablePaymentTypes.includes("existing-card-payment") ||
    availablePaymentTypes.includes("new-card-payment") ||
    availablePaymentTypes.includes("existing-card-payment-with-deposit") ||
    availablePaymentTypes.includes("new-card-payment-with-deposit");

  const hasSelectedSellarCreditAsPaymentOption =
    order.payment.type === "sellar-credit" &&
    availablePaymentTypes.includes("sellar-credit");

  const isPlaceOrderButtonDisabled =
    isPlacingOrder ||
    isValidatingCart ||
    hasOutOfStockItems ||
    !order.payment.type ||
    (isOrderValueGreaterThanCreditBalance &&
      hasSelectedSellarCreditAsPaymentOption);

  const isDeliverySectionIncomplete =
    !hasAcceptedPreorderTerms ||
    !hasAcceptedOutOfOfficeTerms ||
    (isSellarDistribution && !order.distributionContactId);

  const isDisclosureDisabled = isValidatingCart || isDeliverySectionIncomplete;

  const canDisplayDisabledSellarCreditOption =
    isOrderValueGreaterThanCreditBalance && sellarCreditStatus === "approved";

  const clearPayment = (order: CheckoutOrder) => {
    setOrder({
      ...order,
      payment: {
        paymentMethodId: undefined,
        type: undefined,
      },
    });
  };

  const handleFulfillmentTypeChange = ({
    fulfillmentType,
  }: {
    fulfillmentType: CheckoutSelectedFulfillmentType;
  }) => {
    setOrder({
      ...order,
      deliveryType: fulfillmentType,
      // Snap back to the retailer's registered address whenever SD is (re)selected,
      // discarding any address edited under a non-SD method (ENG-5166).
      ...(fulfillmentType === "sellarDistribution" && {
        deliveryAddress: business.DeliveryAddress,
      }),
    });
    updateFulfillmentType(fulfillmentType);
  };

  const handleDistributionContactUpdate = useCallback((id?: number) => {
    setOrder((prev) => ({ ...prev, distributionContactId: id }));
  }, []);

  const nextButtonInDeliverySection = (
    <Button
      className="w-full"
      disabled={isDeliverySectionIncomplete}
      onClick={() => {
        onValidateCheckout();
        track("Checkout Delivery Info Confirmed", {
          retailerName: business.name,
          supplierName: supplier.Business.name,
          retailerId: business.Retailer?.id,
          supplierId: supplier.id,
          sessionId,
        });
        setActiveDisclosure("details");
      }}
    >
      Next
    </Button>
  );

  const nextButtonInOrderDetailsSection = (
    <Button
      className="w-full"
      onClick={() => {
        onValidateCheckout();
        track("Checkout Review Info Confirmed", {
          retailerName: business.name,
          supplierName: supplier.Business.name,
          retailerId: business.Retailer?.id,
          supplierId: supplier.id,
          sessionId,
        });
        setActiveDisclosure("payments");
      }}
    >
      Next
    </Button>
  );

  const depositAmount = calculateCardPaymentDeposit(order.price);
  const totalAmountForStripeFormatted = formatCurrency(order.price);
  const depositAmountForStripeFormatted = formatCurrency(depositAmount);
  const payAmountForStripeFormatted = hasSelectedCardPaymentWithDeposit
    ? depositAmountForStripeFormatted
    : totalAmountForStripeFormatted;

  const defaultPayWithStripeLabel = `Order and Pay ${payAmountForStripeFormatted}`;

  const PlaceOrderWithStripeButton = ({
    label = defaultPayWithStripeLabel,
    className = "w-full",
    idempotencyKey = initialIdempotencyKey,
    price = order.price,
  }: PlaceOrderButtonProps) => (
    <ElementsConsumer>
      {({ stripe, elements }) => {
        return (
          <Button
            onClick={() => {
              return onPlaceOrderWithStripe({
                order: { ...order, price },
                stripe,
                elements,
                options: {
                  idempotencyKey,
                },
              });
            }}
            loading={isPlacingOrder}
            disabled={isPlaceOrderButtonDisabled}
            className={className}
          >
            {label}
          </Button>
        );
      }}
    </ElementsConsumer>
  );

  const PlaceOrderButton = ({
    label = "Place order",
    className = "w-full",
    idempotencyKey = initialIdempotencyKey,
    price = order.price,
    onPlaceOrder,
  }: PlaceOrderButtonProps & {
    onPlaceOrder: PlaceOrderHandler;
  }) => (
    <Button
      onClick={() => {
        return onPlaceOrder({
          order: { ...order, price },
          options: {
            idempotencyKey,
          },
        });
      }}
      loading={isPlacingOrder}
      disabled={isPlaceOrderButtonDisabled}
      className={className}
    >
      {label}
    </Button>
  );

  const renderPlaceOrderButtonInPaymentsSection = (
    props: PlaceOrderButtonProps,
  ) => {
    // Stripe payment
    if (
      (hasSelectedCardPayment || hasSelectedCardPaymentWithDeposit) &&
      canPlaceOrderWithStripe
    ) {
      return <PlaceOrderWithStripeButton {...props} />;
    }

    // Ryft card payments

    if (hasSelectedRyftCardPayment) {
      return (
        <PlaceOrderButton {...props} onPlaceOrder={onPlaceOrderWithRyft} />
      );
    }

    // Non Stripe & Non Ryft payments

    return (
      <PlaceOrderButton {...props} onPlaceOrder={onPlaceOrderWithoutStripe} />
    );
  };

  const showTermsDisclaimer = !priceTierLink && !connection;

  const nbDaysOfCreditForSellarCredit = dayjs()
    .endOf("month")
    .add(30, "days")
    .diff(dayjs(), "days");

  const sellarCreditDueDate = dayjs()
    .add(nbDaysOfCreditForSellarCredit, "days")
    .toDate();

  return (
    <>
      <StripeProvider
        stripeAccountId={stripeAccountId}
        amount={orderSummary.totalCharge}
      >
        <div className="flex flex-col gap-4 overflow-x-hidden bg-transparent pb-10">
          <OrderInformationDisclosure
            isOpen={activeDisclosure === "delivery"}
            position={1}
            title="Delivery"
            onClick={() => setActiveDisclosure("delivery")}
            disabled={isValidatingCart}
          >
            <OrderInformationButton
              startIcon="MAP_MARKER"
              endIcon={isSellarDistribution ? undefined : "PENCIL"}
              title="Delivery address"
              subtitle={mapToAddress(order.deliveryAddress)}
              disabled={isSellarDistribution}
              onClick={
                isSellarDistribution
                  ? undefined
                  : () => setActiveDialog("deliveryAddress")
              }
              dialog={
                isSellarDistribution ? undefined : (
                  <CheckoutDialog
                    isOpen={activeDialog === "deliveryAddress"}
                    onDismiss={() => setActiveDialog(null)}
                    title="Change delivery address"
                    description="This will only apply to your current order"
                  >
                    <CheckoutEditAddressForm
                      initialData={order.deliveryAddress}
                      onConfirm={(deliveryAddress) => {
                        setOrder({
                          ...order,
                          deliveryAddress,
                        });
                        setActiveDialog(null);
                      }}
                    />
                  </CheckoutDialog>
                )
              }
            />
            {checkoutFulfillmentType !== "sellarDistribution" && (
              <OrderInformationButton
                startIcon="PHONE"
                endIcon="PENCIL"
                title="Phone number"
                subtitle={order.phoneNumber}
                onClick={() => setActiveDialog("phoneNumber")}
                dialog={
                  <CheckoutDialog
                    isOpen={activeDialog === "phoneNumber"}
                    onDismiss={() => setActiveDialog(null)}
                    title="Edit mobile number"
                    description="This is the number you will receive your delivery updates through"
                  >
                    <CheckoutEditPhoneNumberForm
                      initialData={order.phoneNumber}
                      onConfirm={(phoneNumber) => {
                        setOrder({
                          ...order,
                          phoneNumber,
                        });
                        setActiveDialog(null);
                      }}
                    />
                  </CheckoutDialog>
                }
              />
            )}
            <OrderInformationButton
              startIcon="NOTE_TEXT_OUTLINE"
              endIcon="PENCIL"
              title="Delivery instructions"
              subtitle={order.deliveryInstructions}
              onClick={() => setActiveDialog("deliveryInstructions")}
              dialog={
                <CheckoutDialog
                  isOpen={activeDialog === "deliveryInstructions"}
                  onDismiss={() => setActiveDialog(null)}
                  title="Add a message"
                  description=""
                >
                  <CheckoutCustomCommentForm
                    initialMode="delivery-instructions"
                    data={{
                      deliveryInstructions: order.deliveryInstructions,
                      orderComment: order.comment,
                    }}
                    onSaveDeliveryInstructions={(message) => {
                      setOrder({
                        ...order,
                        deliveryInstructions: message,
                      });
                      setActiveDialog(null);
                    }}
                    onSaveOrderComment={(message) => {
                      setOrder({
                        ...order,
                        comment: message,
                      });
                      setActiveDialog(null);
                    }}
                  />
                </CheckoutDialog>
              }
            />
            <OrderInformationButton
              startIcon="COMMENT_TEXT_OUTLINE"
              endIcon="PENCIL"
              title="Add a comment"
              subtitle={order.comment}
              onClick={() => setActiveDialog("comment")}
              dialog={
                <CheckoutDialog
                  isOpen={activeDialog === "comment"}
                  onDismiss={() => setActiveDialog(null)}
                  title="Add a message"
                  description=""
                >
                  <CheckoutCustomCommentForm
                    initialMode="order-comments"
                    data={{
                      deliveryInstructions: order.deliveryInstructions,
                      orderComment: order.comment,
                    }}
                    onSaveDeliveryInstructions={(message) => {
                      setOrder({
                        ...order,
                        deliveryInstructions: message,
                      });
                      setActiveDialog(null);
                    }}
                    onSaveOrderComment={(message) => {
                      setOrder({
                        ...order,
                        comment: message,
                      });
                      setActiveDialog(null);
                    }}
                  />
                </CheckoutDialog>
              }
            />

            {checkoutFulfillmentType === "sellarDistribution" && (
              <div className="flex flex-col gap-1">
                <InputLabel>Delivery contact</InputLabel>
                <p className="text-zinc-500">
                  Let us know who should be the point of contact on your day of
                  delivery
                </p>
                <SellarDistributionContactCard
                  contactId={order.distributionContactId}
                  onContactUpdate={handleDistributionContactUpdate}
                />
              </div>
            )}

            {/* NOTE: delivery options not shown for supplier with only card payment with deposit */}
            <div className="flex flex-col gap-1">
              <InputLabel>Delivery Method</InputLabel>

              {!supplier.onlyAllowCardPaymentWithDeposit && (
                <p className="text-zinc-500">
                  Your chosen delivery method is shown. you can change your
                  delivery method by clicking the edit icon
                </p>
              )}

              {isUnbundling && storefront?.deliveryMethods ? (
                <CheckoutFulfillmentTypeSelector
                  supplier={supplier}
                  order={basketOrder}
                  value={checkoutFulfillmentType}
                  supplierStorefront={storefront}
                  supplierRelationshipFulfillmentType={
                    connection?.fulfillmentType
                  }
                  onChange={handleFulfillmentTypeChange}
                  renderFooter={({ eligibleFulfillmentTypes }) => {
                    if (!storefront.sdDeliveryArea) {
                      return null;
                    }
                    return (
                      <DistributionEducationDeliveryMethodAlert
                        eligibleFulfillmentTypes={eligibleFulfillmentTypes}
                        selectedFulfillmentType={order.deliveryType}
                        supplier={supplier}
                        buyerPostcode={buyerPostcode}
                        basketOrder={basketOrder}
                      />
                    );
                  }}
                />
              ) : (
                <FulfillmentTypeRadioSelectorV2
                  deliveryArea={storefront?.DeliveryArea ?? undefined}
                  directDeliveryArea={storefront?.directDeliveryArea}
                  sdDeliveryArea={storefront?.sdDeliveryArea}
                  supplier={supplier}
                  order={basketOrder}
                  priceTierLink={priceTierLink}
                  value={checkoutFulfillmentType}
                  supplierRelationshipFulfillmentType={
                    connection?.fulfillmentType
                  }
                  buyerPostcode={buyerPostcode}
                  onChange={handleFulfillmentTypeChange}
                  renderFooter={({ eligibleFulfillmentTypes }) => {
                    if (!storefront?.sdDeliveryArea) {
                      return null;
                    }
                    return (
                      <DistributionEducationDeliveryMethodAlert
                        eligibleFulfillmentTypes={eligibleFulfillmentTypes}
                        selectedFulfillmentType={order.deliveryType}
                        supplier={supplier}
                        buyerPostcode={buyerPostcode}
                        basketOrder={basketOrder}
                      />
                    );
                  }}
                />
              )}
            </div>

            {checkoutFulfillmentType === "sellarDistribution" && (
              <div className="flex flex-col gap-1">
                <InputLabel>Delivery time slot preference</InputLabel>
                <p className="text-zinc-500">
                  Let us know your preferred time of day to receive deliveries
                  so that we can aim to meet your preference
                </p>
                <div className="flex gap-4">
                  {(
                    [
                      {
                        key: "am",
                        label: "AM",
                      },
                      {
                        key: "pm",
                        label: "Early PM",
                      },
                      {
                        key: "allDay",
                        label: "All day",
                      },
                    ] as const
                  ).map((option) => (
                    <label
                      key={option.key}
                      className={twMerge(
                        "flex w-full cursor-pointer items-center space-x-3 rounded-lg border bg-white px-4 py-3 transition hover:bg-zinc-100",
                        order.timePreference === option.key
                          ? "border-zinc-400 bg-zinc-200"
                          : "border-white",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={order.timePreference === option.key}
                        onChange={(e) => {
                          setOrder({
                            ...order,
                            timePreference: e.target.checked
                              ? option.key
                              : null,
                          });
                        }}
                        className="h-4 w-4 rounded-lg accent-zinc-800 focus:ring-2 focus:ring-zinc-500"
                      />
                      <span className="text-gray-700 capitalize">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {isPreorder && (
              <AcknowledgeRequirement
                onChange={setHasAcknowledgedPreorder}
                checked={hasAcknowledgedPreorder}
              >
                <div className="font-semibold">
                  Shipping date: {dayjs(latestPreorderDate).format("ddd D MMM")}
                </div>
                <p>
                  I understand this is the earliest date my order will be
                  dispatched as it contains 1 or more products only available
                  for pre-order.
                </p>
                <p>
                  If you would like products that are not on pre-order sooner
                  please place a separate order.
                </p>
              </AcknowledgeRequirement>
            )}
            {canDisplayOutOfOfficeAcknowledgement && (
              <AcknowledgeRequirement
                onChange={setHasAcknowledgedOutOfOffice}
                checked={hasAcknowledgedOutOfOffice}
              >
                <div className="font-semibold">
                  Shipping date:{" "}
                  {dayjs(outOfOfficeShippingDate).format("ddd D MMM")}
                </div>
                <p>
                  I understand this is the earliest I will receive my order as{" "}
                  {supplierName} are currently Out of Office.
                </p>
              </AcknowledgeRequirement>
            )}
            {isDesktop && nextButtonInDeliverySection}
          </OrderInformationDisclosure>
          <OrderInformationDisclosure
            isOpen={activeDisclosure === "details"}
            position={2}
            title="Order Details"
            onClick={() => setActiveDisclosure("details")}
            disabled={isDisclosureDisabled}
          >
            <OrderInformationButton
              startIcon="POUND"
              endIcon="PENCIL"
              title="Purchase order number"
              subtitle={order.purchaseOrderNumber}
              onClick={() => setActiveDialog("purchaseOrder")}
              dialog={
                <CheckoutDialog
                  isOpen={activeDialog === "purchaseOrder"}
                  onDismiss={() => setActiveDialog(null)}
                  title="Edit purchase order number"
                  description="Include a purchase order number if you'd like it to be included in your invoice"
                >
                  <CheckoutEditPurchaseOrderForm
                    initialData={order.purchaseOrderNumber}
                    onConfirm={(purchaseOrderNumber) => {
                      setOrder({
                        ...order,
                        purchaseOrderNumber,
                      });
                      setActiveDialog(null);
                    }}
                  />
                </CheckoutDialog>
              }
            />
            <OrderInformationButton
              startIcon="FILE_MARKER_OUTLINE"
              endIcon="PENCIL"
              title="Invoice address"
              subtitle={mapToAddress(order.invoiceAddress)}
              onClick={() => setActiveDialog("invoiceAddress")}
              dialog={
                <CheckoutDialog
                  isOpen={activeDialog === "invoiceAddress"}
                  onDismiss={() => setActiveDialog(null)}
                  title="Change invoice address"
                  description="This will only apply to your current order"
                >
                  <CheckoutEditAddressForm
                    initialData={order.invoiceAddress}
                    onConfirm={(invoiceAddress) => {
                      setOrder({
                        ...order,
                        invoiceAddress,
                      });
                      setActiveDialog(null);
                    }}
                  />
                </CheckoutDialog>
              }
            />
            <OrderInformationButton
              startIcon="GLASS_MUG_VARIANT"
              endIcon="PENCIL"
              title="Draught duty requirements"
              subtitle={mapToDraughtRequirements(order.draughtDuty)}
              onClick={() => setActiveDialog("draughtDuty")}
              visible={hasDraughtDutyOrderItems}
              dialog={
                <CheckoutDialog
                  isOpen={activeDialog === "draughtDuty"}
                  onDismiss={() => setActiveDialog(null)}
                  title="Draught duty requirements"
                  description=""
                >
                  <CheckoutEditDraughtDutyForm
                    initialData={order.draughtDuty}
                    onConfirm={(draughtDuty) => {
                      setOrder({
                        ...order,
                        draughtDuty,
                      });
                      setActiveDialog(null);
                    }}
                  />
                </CheckoutDialog>
              }
            />

            {isDesktop && nextButtonInOrderDetailsSection}
          </OrderInformationDisclosure>
          <OrderInformationDisclosure
            isOpen={activeDisclosure === "payments"}
            position={3}
            title="Payment"
            disabled={isDisclosureDisabled}
            onClick={() => {
              setActiveDisclosure("payments");
            }}
          >
            <div className="flex flex-col gap-4">
              {availablePaymentTypes.includes("sellar-credit") &&
                canDisplayDisabledSellarCreditOption && (
                  <SellarCreditOptionDisabled
                    canUserViewSellarCreditBalance={
                      canUserViewSellarCreditBalance
                    }
                    sellarCreditBalance={sellarCreditBalance}
                  />
                )}
              <SellarCreditPaymentOption
                selected={order.payment.type === "sellar-credit"}
                visible={availablePaymentTypes.includes("sellar-credit")}
                dueDate={sellarCreditDueDate}
                canUserViewBalance={canUserViewSellarCreditBalance}
                balance={sellarCreditBalance}
                onSelect={() => {
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: undefined,
                      type: "sellar-credit",
                    },
                  });
                }}
                onDeselect={() => {
                  clearPayment(order);
                }}
              >
                {creditProvider === "two" &&
                  isCompanyVerified === false &&
                  sellarCreditStatus === "approved" && (
                    <div className="p-4">
                      <TwoSellarCreditVerificationRequiredAlert
                        fontSize="text-base"
                        buttonFontSize="text-sm"
                        message="To keep using Sellar Credit, you need to complete verification with our new credit provider, Two Inc."
                      />
                    </div>
                  )}
                <SellarCreditContent
                  total={orderSummary.total}
                  dueDate={sellarCreditDueDate}
                  nbDaysOfCredit={nbDaysOfCreditForSellarCredit}
                />
              </SellarCreditPaymentOption>
              <SupplierCreditPaymentOption
                selected={order.payment.type === "credit-account"}
                visible={availablePaymentTypes.includes("credit-account")}
                supplierName={supplierName}
                title={
                  isImplicitProformaEnabled
                    ? `Pay by supplier invoice - ${connectionPaymentDue} day terms`
                    : undefined
                }
                description={
                  isImplicitProformaEnabled ? (
                    <span className="text-start text-zinc-500">
                      {supplierName} will issue the VAT invoice for this order
                      outside of Sellar. Payment is due within{" "}
                      {connectionPaymentDue} days of your order being{" "}
                      {supplier.invoiceCreationStage === "confirmed"
                        ? "finalised"
                        : supplier.invoiceCreationStage}
                      .
                    </span>
                  ) : (
                    <CreditAccountDescription
                      paymentDue={connectionPaymentDue}
                      paymentDueCalculateFrom={
                        connectionPaymentDueCalculateFrom
                      }
                      supplier={supplier}
                    />
                  )
                }
                onSelect={() => {
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: undefined,
                      type: "credit-account",
                    },
                    ...(isPaymentLinkRequestEnabled && {
                      invoiceMethod: "manual-invoice" as const,
                    }),
                  });
                }}
                onDeselect={() => {
                  clearPayment(order);
                }}
              >
                <div className="px-4 pb-4">
                  <InvoiceSettings
                    id="credit-account"
                    invoiceMethod={order.invoiceMethod}
                    invoiceMethodOptions={invoiceMethodOptions}
                    onChangeInvoiceMethod={(invoiceMethod) => {
                      setOrder({
                        ...order,
                        invoiceMethod,
                      });
                    }}
                    orderTotal={order.price}
                    invoiceEmails={invoiceEmails}
                    hasEmailsChanged={hasInvoiceEmailsChanged}
                    onChangeInvoiceEmails={setInvoiceEmails}
                    onSubmitInvoiceEmails={() =>
                      onUpdateInvoiceEmails(invoiceEmails)
                    }
                    stripeAccountId={stripeAccountId}
                    {...(stripeAccountId && {
                      paymentLinkRequested: order.paymentLinkRequested,
                      onChangePaymentLinkRequested: (checked: boolean) => {
                        setOrder({
                          ...order,
                          paymentLinkRequested: checked,
                        });
                      },
                    })}
                  />
                </div>
              </SupplierCreditPaymentOption>
              <PaymentOptionCollapsible
                selected={order.payment.type === "pay-on-delivery"}
                visible={availablePaymentTypes.includes("pay-on-delivery")}
                trigger={
                  <div className="flex w-full flex-col gap-2">
                    <div className="flex w-full justify-between">
                      <span className="text-start font-semibold">
                        {isImplicitProformaEnabled
                          ? "Pay by supplier invoice - on delivery"
                          : "Pay on delivery"}
                      </span>
                      <BadgeOrange>Pay on delivery</BadgeOrange>
                    </div>
                    <span className="text-start text-zinc-500">
                      {isImplicitProformaEnabled
                        ? `${supplierName} will issue the VAT invoice for this order outside of Sellar. Payment is due upon delivery of your order.`
                        : `Make your payment to ${supplierName} by the day of delivery. Your invoice will be issued when your order is finalised.`}
                    </span>
                  </div>
                }
                onOpen={() => {
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: undefined,
                      type: "pay-on-delivery",
                    },
                  });
                }}
                onClose={() => {
                  clearPayment(order);
                }}
              >
                <div className="px-4 pb-4">
                  <InvoiceSettings
                    id="pay-on-delivery"
                    invoiceMethod={order.invoiceMethod}
                    invoiceMethodOptions={invoiceMethodOptions}
                    onChangeInvoiceMethod={(invoiceMethod) => {
                      setOrder({
                        ...order,
                        invoiceMethod,
                      });
                    }}
                    orderTotal={order.price}
                    invoiceEmails={invoiceEmails}
                    hasEmailsChanged={hasInvoiceEmailsChanged}
                    onChangeInvoiceEmails={setInvoiceEmails}
                    onSubmitInvoiceEmails={() =>
                      onUpdateInvoiceEmails(invoiceEmails)
                    }
                  />
                </div>
              </PaymentOptionCollapsible>
              <PayAtCheckoutPaymentOption
                selected={[
                  order.payment.type === "new-card-payment",
                  order.payment.type === "existing-card-payment",
                ].some(Boolean)}
                visible={[
                  availablePaymentTypes.includes("existing-card-payment") ||
                    availablePaymentTypes.includes("new-card-payment"),
                ].every(Boolean)}
                supplierName={supplierName}
                title={
                  isImplicitProformaEnabled
                    ? "Pay supplier now by card"
                    : undefined
                }
                description={
                  isImplicitProformaEnabled
                    ? `Your secure card payment goes directly to ${supplierName} who will issue the VAT invoice for this order outside of Sellar.`
                    : undefined
                }
                onSelect={() => {
                  const stripePaymentMethodId =
                    paymentMethods[0]?.stripePaymentMethodId;
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: stripePaymentMethodId,
                      type: stripePaymentMethodId
                        ? "existing-card-payment"
                        : "new-card-payment",
                    },
                  });
                }}
                onDeselect={() => {
                  clearPayment(order);
                }}
              >
                <div className="px-4 pb-4">
                  <CardPaymentOptions
                    activePayment={
                      order.payment.paymentMethodId
                        ? {
                            type: "existing-card",
                            paymentMethodId: order.payment.paymentMethodId,
                          }
                        : { type: "new-card" }
                    }
                    existingCardPaymentMethods={paymentMethods}
                    onSelectPaymentMethod={(payment) => {
                      setOrder({
                        ...order,
                        payment:
                          payment.type === "existing-card"
                            ? {
                                type: "existing-card-payment",
                                paymentMethodId: payment.paymentMethodId,
                              }
                            : {
                                type: "new-card-payment",
                                paymentMethodId: undefined,
                              },
                      });
                    }}
                  />
                </div>
              </PayAtCheckoutPaymentOption>
              <RyftCardPaymentOption
                title="Pay supplier now with Ryft"
                amount={order.price}
                supplierId={supplier.id}
                description={`Your payment to ${supplierName} will be processed instantly, with no need for any back and forth.`}
                selected={order.payment.type === "new-ryft-card-payment"}
                visible={availablePaymentTypes.includes(
                  "new-ryft-card-payment",
                )}
                onSelect={() => {
                  setOrder({
                    ...order,
                    payment: {
                      type: "new-ryft-card-payment",
                      paymentMethodId: undefined,
                    },
                  });
                }}
                onDeselect={() => {
                  clearPayment(order);
                }}
              />
              <PaymentOptionCollapsible
                selected={[
                  order.payment.type === "new-card-payment-with-deposit",
                  order.payment.type === "existing-card-payment-with-deposit",
                ].some(Boolean)}
                visible={[
                  availablePaymentTypes.includes(
                    "new-card-payment-with-deposit",
                  ) ||
                    availablePaymentTypes.includes(
                      "existing-card-payment-with-deposit",
                    ),
                ].every(Boolean)}
                trigger={
                  <div className="flex w-full flex-1 flex-col gap-2">
                    <div className="flex w-full justify-between">
                      <span className="text-start font-semibold">
                        Pay deposit now by card
                      </span>
                      <BadgeBlue>Pay deposit at checkout</BadgeBlue>
                    </div>
                    <span className="text-start text-zinc-500">
                      You’re paying a 30% deposit of{" "}
                      {formatCurrency(depositAmount)} today. The remaining
                      balance will be automatically charged to your card closer
                      to the delivery date.
                    </span>
                  </div>
                }
                onOpen={() => {
                  const stripePaymentMethodId =
                    paymentMethods[0]?.stripePaymentMethodId;
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: stripePaymentMethodId,
                      type: stripePaymentMethodId
                        ? "existing-card-payment-with-deposit"
                        : "new-card-payment-with-deposit",
                    },
                  });
                }}
                onClose={() => {
                  clearPayment(order);
                }}
              >
                <div className="px-4 pb-4">
                  {activeDisclosure === "payments" && (
                    <CardPaymentOptions
                      useCardElement={true}
                      activePayment={
                        order.payment.paymentMethodId
                          ? {
                              type: "existing-card",
                              paymentMethodId: order.payment.paymentMethodId,
                            }
                          : { type: "new-card" }
                      }
                      existingCardPaymentMethods={paymentMethods}
                      onSelectPaymentMethod={(payment) => {
                        setOrder({
                          ...order,
                          payment:
                            payment.type === "existing-card"
                              ? {
                                  type: "existing-card-payment-with-deposit",
                                  paymentMethodId: payment.paymentMethodId,
                                }
                              : {
                                  type: "new-card-payment-with-deposit",
                                  paymentMethodId: undefined,
                                },
                        });
                      }}
                    />
                  )}
                </div>
              </PaymentOptionCollapsible>

              <PaymentOptionCollapsible
                selected={order.payment.type === "request-proforma-invoice"}
                visible={availablePaymentTypes.includes(
                  "request-proforma-invoice",
                )}
                trigger={
                  <div className="flex w-full flex-1 flex-col gap-2">
                    <div className="flex w-full justify-between">
                      <span className="text-start font-semibold">
                        {isImplicitProformaEnabled
                          ? "Pay by supplier invoice"
                          : "Request a proforma invoice"}
                      </span>
                      <BadgeYellow>Pro-forma</BadgeYellow>
                    </div>
                    <span className="text-start text-zinc-500">
                      {isImplicitProformaEnabled
                        ? `${supplierName} will issue the VAT invoice for this order outside of Sellar. They may request payment before your order is dispatched unless you already have agreed credit terms with them.`
                        : `Make your payment before ${supplierName} dispatches your order.`}
                    </span>
                  </div>
                }
                onOpen={() => {
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: undefined,
                      type: "request-proforma-invoice",
                    },
                    ...(isPaymentLinkRequestEnabled && {
                      invoiceMethod: "manual-invoice" as const,
                    }),
                  });
                }}
                onClose={() => {
                  clearPayment(order);
                }}
              >
                <div className="px-4 pb-4">
                  <InvoiceSettings
                    id="request-proforma-invoice"
                    invoiceMethod={order.invoiceMethod}
                    invoiceMethodOptions={invoiceMethodOptions}
                    onChangeInvoiceMethod={(invoiceMethod) => {
                      setOrder({
                        ...order,
                        invoiceMethod,
                      });
                    }}
                    orderTotal={order.price}
                    invoiceEmails={invoiceEmails}
                    hasEmailsChanged={hasInvoiceEmailsChanged}
                    onChangeInvoiceEmails={setInvoiceEmails}
                    onSubmitInvoiceEmails={() =>
                      onUpdateInvoiceEmails(invoiceEmails)
                    }
                    stripeAccountId={stripeAccountId}
                    paymentLinkRequested={order.paymentLinkRequested}
                    onChangePaymentLinkRequested={(checked) => {
                      setOrder({
                        ...order,
                        paymentLinkRequested: checked,
                      });
                    }}
                  />
                </div>
              </PaymentOptionCollapsible>
              <PaymentOptionCollapsible
                selected={order.payment.type === "request-credit-terms"}
                visible={availablePaymentTypes.includes("request-credit-terms")}
                trigger={
                  <div className="flex w-full flex-1 flex-col gap-2">
                    <span className="text-start font-semibold">
                      I have credit terms with {supplierName}
                    </span>
                    <span className="text-start text-zinc-500">
                      You can place your order using your usual credit terms
                      with {supplierName} if they haven't been assigned on
                      Sellar.
                    </span>
                  </div>
                }
                onOpen={() => {
                  setOrder({
                    ...order,
                    payment: {
                      paymentMethodId: undefined,
                      type: "request-credit-terms",
                    },
                    ...(isPaymentLinkRequestEnabled && {
                      invoiceMethod: "manual-invoice" as const,
                    }),
                  });
                }}
                onClose={() => {
                  clearPayment(order);
                }}
              >
                <div className="flex flex-col gap-2 px-4 pb-4">
                  <RequestCreditTermsForm
                    businessName={supplierName}
                    onChange={(terms) => {
                      setOrder({
                        ...order,
                        paymentTermsRequest: terms,
                      });
                    }}
                    terms={order.paymentTermsRequest}
                  />
                  <InvoiceSettings
                    id="request-credit-terms"
                    invoiceMethod={order.invoiceMethod}
                    invoiceMethodOptions={invoiceMethodOptions}
                    onChangeInvoiceMethod={(invoiceMethod) => {
                      setOrder({
                        ...order,
                        invoiceMethod,
                      });
                    }}
                    orderTotal={order.price}
                    invoiceEmails={invoiceEmails}
                    hasEmailsChanged={hasInvoiceEmailsChanged}
                    onChangeInvoiceEmails={setInvoiceEmails}
                    onSubmitInvoiceEmails={() =>
                      onUpdateInvoiceEmails(invoiceEmails)
                    }
                    {...(stripeAccountId && {
                      paymentLinkRequested: order.paymentLinkRequested,
                      onChangePaymentLinkRequested: (checked: boolean) => {
                        setOrder({
                          ...order,
                          paymentLinkRequested: checked,
                        });
                      },
                    })}
                  />
                </div>
              </PaymentOptionCollapsible>
            </div>

            {!hasMailingListPermission && (
              <MailingListConfirmation
                businessName={supplierName}
                checked={order.mailingListPermission}
                onChange={(mailingListPermission) => {
                  setOrder({
                    ...order,
                    mailingListPermission,
                  });
                }}
              />
            )}
            <div className="flex flex-1 gap-2">
              {isDesktop && renderPlaceOrderButtonInPaymentsSection({})}
            </div>
            {showTermsDisclaimer && <TermsDisclaimer />}
          </OrderInformationDisclosure>
        </div>
        {!isDesktop && (
          <div className="fixed inset-x-0 bottom-4 mx-4 flex items-center justify-center">
            {activeDisclosure === "delivery" && nextButtonInDeliverySection}
            {activeDisclosure === "details" && nextButtonInOrderDetailsSection}
            {activeDisclosure === "payments" &&
              renderPlaceOrderButtonInPaymentsSection({})}
          </div>
        )}
        {placeOrderMode.type === "price-mismatch" && (
          <PriceMismatchDialog
            priceMismatchMode={placeOrderMode}
            priceMismatchAcceptButton={renderPlaceOrderButtonInPaymentsSection({
              className: "w-fit",
              label: "Yes",
              price: placeOrderMode.data.serverPrice,
              idempotencyKey: v4(),
            })}
            priceMismatchDeclineButton={
              <Button
                onClick={() => {
                  db.draftOrders.put({
                    ...basketOrder,
                    RetailerId: basketOrder?.RetailerId ?? null,
                    // Reset the idempotency key so new orders can be placed with ease
                    idempotencyKey: v4(),
                  });
                  navigate(-1);
                }}
                color="red"
                disabled={isPlacingOrder}
              >
                No
              </Button>
            }
            supplierName={supplierName}
            onDismiss={() => {
              // Do nothing - We don't want users to be able to close this dialog
            }}
          />
        )}
      </StripeProvider>
    </>
  );
};

const TermsDisclaimer = () => {
  return (
    <p className="pb-4 text-sm lg:pb-0">
      By placing this order, you agree to both the{" "}
      <Anchor href="https://www.sellar.io/terms" target="_blank">
        Sellar Terms and Conditions
      </Anchor>{" "}
      and the{" "}
      <Anchor
        href="https://sellar.io/sellar-market-supply-of-goods-terms-and-conditions"
        target="_blank"
      >
        Sellar Market Supply of Goods Terms & Conditions
      </Anchor>
    </p>
  );
};

type OrderInformationDisclosureProps = {
  position: number;
  children: ReactNode;
  isOpen: boolean;
  title: string;
  disabled: boolean;
  onClick: () => void;
};
const OrderInformationDisclosure = ({
  title,
  position,
  children,
  isOpen,
  disabled,
  onClick,
}: OrderInformationDisclosureProps) => {
  // Controlled accordion: open state is owned by the parent, so animate with
  // SlideTransition rather than Disclosure's internal open (which is uncontrolled).
  return (
    <div className="flex flex-1 flex-col">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full flex-1 cursor-pointer gap-4 rounded-t-lg bg-gray-100 p-4 disabled:cursor-not-allowed"
        disabled={disabled}
        aria-expanded={isOpen}
      >
        <span className="text-lg font-semibold text-gray-400">{position}</span>
        <span className="text-lg font-semibold text-gray-800">{title}</span>
      </button>
      <SlideTransition show={isOpen} parentClass="duration-200 ease-out">
        <div className="relative flex flex-col gap-4 bg-gray-100 p-4">
          {children}
        </div>
      </SlideTransition>
    </div>
  );
};

interface OrderInformationButtonProps extends ComponentProps<"button"> {
  startIcon: IconType;
  endIcon?: IconType;
  endText?: ReactNode;
  subtitle?: ReactNode;
  subtitleFallback?: string;
  dialog?: ReactNode;
  visible?: boolean;
  endBadge?: ReactNode;
}
export const OrderInformationButton = ({
  startIcon,
  endIcon,
  endBadge,
  endText,
  title,
  subtitle,
  dialog,
  subtitleFallback = "Not set",
  visible = true,
  ...props
}: OrderInformationButtonProps) => {
  if (!visible) return null;
  return (
    <>
      <button
        type="button"
        className="group flex min-h-20 flex-1 items-center justify-between gap-2 rounded-lg bg-white p-4 hover:bg-gray-50"
        {...props}
      >
        <div className="flex flex-1 items-center gap-4">
          <Icon
            icon={startIcon}
            size={0.7}
            className="text-zinc-500 group-hover:text-zinc-800"
          />
          <div className="flex flex-1 flex-col">
            <div className="flex justify-between">
              <span className="text-left font-semibold">{title}</span>
              <div className="flex items-center gap-4">
                {endBadge && endBadge}
                {endText && endText}
                {endIcon && (
                  <Icon
                    icon={endIcon}
                    size={0.7}
                    className="text-zinc-500 group-hover:text-zinc-800"
                  />
                )}
              </div>
            </div>
            {subtitle ? (
              <span className="text-start">{subtitle}</span>
            ) : (
              <span className="text-start text-zinc-400">
                {subtitleFallback}
              </span>
            )}
          </div>
        </div>
      </button>
      {dialog}
    </>
  );
};

type PaymentOptionCollapsibleProps = {
  selected: boolean;
  children: ReactNode;
  trigger: ReactNode;
  visible: boolean;
  onOpen: () => void;
  onClose: () => void;
};
const PaymentOptionCollapsible = ({
  selected,
  visible,
  trigger,
  children,
  onOpen,
  onClose,
}: PaymentOptionCollapsibleProps) => {
  if (!visible) return null;
  // Controlled by payment selection — slide height with the external `selected` flag.
  return (
    <div className="group flex flex-1 flex-col">
      <button
        type="button"
        onClick={selected ? onClose : onOpen}
        className={twMerge(
          "flex w-full flex-1 flex-col items-start rounded-lg border-zinc-800 bg-white p-4 hover:bg-gray-50",
          selected && "rounded-b-none border-x border-t hover:bg-white",
        )}
        aria-expanded={selected}
      >
        {trigger}
      </button>
      <SlideTransition show={selected} parentClass="duration-200 ease-out">
        <div
          className={twMerge(
            "relative flex w-full flex-1 flex-col bg-white",
            selected && "rounded-b-lg border-x border-b border-zinc-800",
          )}
        >
          {children}
        </div>
      </SlideTransition>
    </div>
  );
};

const creditOptions: Array<{
  label: string;
  value: CheckoutCreditTerms["paymentDueCalculateFrom"];
}> = [
  {
    label: "End of Following Month",
    value: "endOfFollowingMonth",
  },
  {
    label: "End of Month",
    value: "endOfMonth",
  },
  {
    label: "Invoice Issue Date",
    value: "invoiceIssueDate",
  },
];

type RequestCreditTermsFormProps = {
  terms: CheckoutCreditTerms;
  businessName: string;
  onChange: (terms: CheckoutCreditTerms) => void;
};
const RequestCreditTermsForm = ({
  terms,
  onChange,
  businessName,
}: RequestCreditTermsFormProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 rounded-lg bg-gray-200 p-4">
        <div className="flex flex-1 flex-col flex-wrap gap-2 lg:flex-row lg:items-center">
          <InputContainer className="lg:w-20">
            <InputNumber
              min={0}
              defaultValue={terms.paymentDue}
              onChange={(event) => {
                onChange({
                  ...terms,
                  paymentDue: Number(event.target.value),
                });
              }}
            />
          </InputContainer>
          <span>days credit. Calculated from</span>
          <InputContainer className="flex flex-1 lg:min-w-0">
            <InputSelect
              onChange={(event) => {
                onChange({
                  ...terms,
                  paymentDueCalculateFrom: event.target
                    .value as CheckoutCreditTerms["paymentDueCalculateFrom"],
                });
              }}
              defaultValue={terms.paymentDueCalculateFrom}
            >
              {creditOptions.map((option) => (
                <InputOption key={option.value} value={option.value}>
                  {option.label}
                </InputOption>
              ))}
            </InputSelect>
          </InputContainer>
        </div>
        <Alert
          type="information"
          message={
            <span>
              <strong>Please note:</strong> if you place your order using terms
              that have not been agreed with {businessName}, you may be asked to
              pay pro-forma, or your order may be cancelled
            </span>
          }
        />
      </div>
    </div>
  );
};

type AcknowledgeRequirementProps = {
  checked: boolean;
  children: ReactNode;
  onChange: (checked: boolean) => void;
};

const AcknowledgeRequirement = ({
  checked,
  children,
  onChange,
}: AcknowledgeRequirementProps) => {
  return (
    <label className="flex gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4">
      <InputCheckbox
        variant="circular"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="flex flex-col">{children}</div>
    </label>
  );
};

type MailingListConfirmationProps = {
  businessName: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};
const MailingListConfirmation = ({
  businessName,
  checked,
  onChange,
}: MailingListConfirmationProps) => {
  return (
    <label className="flex items-center gap-2 p-2">
      <InputCheckbox
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="min-h-3 min-w-3"
      />
      <p>
        Yes, I'd like to receive updates from {businessName} via email. You can
        subscribe / unsubscribe any time
      </p>
    </label>
  );
};

type GetAvailableInvoiceMethodOptions = {
  stripeAccountId: string | undefined;
};

const mapToAvailableInvoiceMethods = ({
  stripeAccountId,
}: GetAvailableInvoiceMethodOptions): InvoiceMethodOption[] => {
  const invoiceMethodOptions: InvoiceMethodOption[] = [];
  const hasStripeAccountId = Boolean(stripeAccountId);

  if (hasStripeAccountId) {
    invoiceMethodOptions.push({
      label: "Card payment link",
      type: "stripe-invoice",
    });
  }

  invoiceMethodOptions.push({
    label: "BACS manual invoice",
    type: "manual-invoice",
  });

  return invoiceMethodOptions;
};

const mapToAddress = (address: CheckoutAddress) => {
  return [
    address.name,
    address.street,
    address.city,
    address.postalCode,
    address.country,
  ]
    .map((addressPart) => addressPart.trim())
    .filter(Boolean)
    .join(", ");
};

const CreditAccountDescription = ({
  supplier,
  paymentDue,
  paymentDueCalculateFrom,
}: {
  supplier: CheckoutProps["supplier"];
  paymentDue: number;
  paymentDueCalculateFrom: SupplierRelationshipGetResponseType["paymentDueCalculateFrom"];
}) => {
  const supplierName = supplier.Business.name;
  const state =
    supplier.invoiceCreationStage === "confirmed"
      ? "finalised"
      : supplier.invoiceCreationStage;
  switch (paymentDueCalculateFrom) {
    case "invoiceIssueDate":
      return (
        <span className="text-start text-zinc-500">
          Make your payment to {supplierName} within {paymentDue} days of your
          order being {state}.
        </span>
      );
    case "endOfMonth":
      return (
        <span className="text-start text-zinc-500">
          Make your payment to {supplierName} {paymentDue} days after the end of
          the current month. Your invoice will be issued when your order is{" "}
          {state}.
        </span>
      );
    case "endOfFollowingMonth":
      return (
        <span className="text-start text-zinc-500">
          Make your payment to {supplierName} {paymentDue} days after the end of
          the following month. Your invoice will be issued when your order is{" "}
          {state}.
        </span>
      );
    default:
      <span className="text-start text-zinc-500">
        Make your payment to {supplierName} within {paymentDue} days.
      </span>;
  }
};

const updatePreselectedPaymentOption = ({
  availablePaymentTypes,
  initialCheckoutOrder,
  isOrderValueGreaterThanCreditBalance,
}: {
  availablePaymentTypes: CheckoutPaymentType[];
  initialCheckoutOrder: CheckoutOrder;
  isOrderValueGreaterThanCreditBalance: boolean;
}): CheckoutOrder => {
  const indexOfSellarCreditPaymentType = availablePaymentTypes.findIndex(
    (paymentType) => paymentType === "sellar-credit",
  );

  const canUseSellarCreditAsPaymentOption = indexOfSellarCreditPaymentType > -1;

  //If there's a sellar credit payment type and there's enough balance, preselect it
  if (
    canUseSellarCreditAsPaymentOption &&
    !isOrderValueGreaterThanCreditBalance
  ) {
    return {
      ...initialCheckoutOrder,
      payment: {
        type: availablePaymentTypes[indexOfSellarCreditPaymentType],
        paymentMethodId: undefined,
      },
    };
  }

  //If we have only one payment type, preselect it
  if (availablePaymentTypes.length === 1) {
    return {
      ...initialCheckoutOrder,
      payment: {
        type: availablePaymentTypes[0],
        paymentMethodId: undefined,
      },
    };
  }

  return initialCheckoutOrder;
};

const useCalculateShippingDateForOutOfOffice = ({
  supplier,
  deliveryType,
  deliveryArea,
  latestAvailablePreOrderFromDate,
  productIds,
  buyerPostcode,
}: {
  supplier: SupplierGetResponseType;
  deliveryType: CheckoutOrder["deliveryType"];
  deliveryArea: SupplierStorefrontV2GetResponseType["DeliveryArea"];
  latestAvailablePreOrderFromDate: Date | null;
  productIds: number[];
  buyerPostcode: string;
}): Date | null => {
  const currentDate = useCurrentDate();
  const outOfOfficeReturnDate =
    supplier.SupplierOutOfOffice?.returnDate ?? null;
  const courierDeliveryScheduleQuery = useCourierDeliveryScheduleList(
    {
      query: [
        {
          value: supplier.id,
          type: "SupplierId",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
      sortDir: "desc",
    },
    {
      enabled: Boolean(supplier.id) && deliveryType === "courier",
    },
  );

  const nextStartDate = getNextStartDate({
    latestAvailablePreOrderFromDate,
    outOfOfficeReturnDate,
    courierDeliveryScheduleStartDate: null,
    currentDate,
  });

  const { nextDeliveryDate: sdDeliveryDate } = useDistributionDeliveryEstimate({
    supplierId: supplier.id,
    buyerPostcode,
    productIds,
  });

  if (deliveryType === "direct") {
    if (!deliveryArea) {
      return nextStartDate;
    }

    return (
      getNextDirectDeliveryDate({
        deliveryArea,
        startDateReference: nextStartDate,
      })?.deliveryDate ?? null
    );
  }

  if (deliveryType === "courier") {
    const courierDeliverySchedule =
      courierDeliveryScheduleQuery.data?.pages[0]?.[0];

    if (!courierDeliverySchedule) {
      return nextStartDate;
    }

    const courierDeliveryCutoffTime = dayjs(
      courierDeliverySchedule.orderCutOffTime,
    )
      .utc()
      .format("HH:mm");
    const nextCourierDispatchAndDeliveryDates =
      getNextCourierDispatchAndDeliveryDates({
        orderCutOffTime: courierDeliveryCutoffTime,
        courierDaysSchedule:
          courierDeliverySchedule.CourierDeliveryScheduleDay.map(
            (CourierDeliveryScheduleDay) => CourierDeliveryScheduleDay.day,
          ).map((day: daysOfWeek) => ({ day })),
        dispatchDaysAfterConfirmation:
          courierDeliverySchedule.dispatchDaysAfterConfirmation,
        deliveryDaysAfterDispatch:
          courierDeliverySchedule.deliveryDaysAfterDispatch,
        startDateReference: nextStartDate,
        allowSaturdayDelivery: courierDeliverySchedule.allowSaturdayDelivery,
      });

    return (
      nextCourierDispatchAndDeliveryDates?.estimatedDeliveryDate.toDate() ??
      nextStartDate
    );
  }

  if (deliveryType === "sellarDistribution") {
    return sdDeliveryDate;
  }

  return nextStartDate;
};

type SellarCreditOptionDisabledProps = {
  canUserViewSellarCreditBalance: boolean;
  sellarCreditBalance: number;
};
const SellarCreditOptionDisabled = ({
  canUserViewSellarCreditBalance,
  sellarCreditBalance,
}: SellarCreditOptionDisabledProps) => {
  return (
    <div className="flex w-full flex-col items-start gap-1 rounded-md border border-zinc-300 bg-zinc-50 p-4">
      <div className="flex w-full items-center justify-center">
        <p className="flex-1 text-start font-semibold text-zinc-800 md:w-auto">
          <span>Payment using</span>{" "}
          <span className="inline-flex gap-1 bg-linear-to-r from-[#B557F1] to-[#D213A6] bg-clip-text text-transparent">
            <img src={sellarLogoNew} className="relative top-0.5 h-4 w-4" />
            <span>Sellar Credit</span>
          </span>{" "}
          <span>unavailable</span>
        </p>
        <Badge className="bg-purple-100">
          <span className="text-[#C637CB]">Sellar Credit</span>
        </Badge>
      </div>
      <p className="text-start text-zinc-600">
        Your order total is greater than your available Sellar Credit balance.
        Please select a different payment option below.
      </p>
      {canUserViewSellarCreditBalance && (
        <p className="text-sm text-zinc-600">
          Balance available {formatCurrency(sellarCreditBalance)}
        </p>
      )}
    </div>
  );
};

const PriceMismatchDialog = ({
  priceMismatchMode,
  priceMismatchAcceptButton,
  priceMismatchDeclineButton,
  supplierName,
  onDismiss,
}: {
  priceMismatchMode: PriceMismatchMode;
  priceMismatchAcceptButton: ReactNode;
  priceMismatchDeclineButton: ReactNode;
  supplierName: string;
  onDismiss: () => void;
}) => {
  return (
    <Dialog
      active={priceMismatchMode.type === "price-mismatch"}
      onDismiss={onDismiss}
      className="m-4 h-fit"
    >
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Your order total has changed"
        description=""
      />
      <DialogContent>
        <div className="flex flex-col gap-4 backdrop-blur-xs">
          <p className="">
            <strong>{supplierName}</strong> may have made a change to their
            pricing, discounts or delivery charges since you added items to your
            basket.
          </p>
          <p>
            Your new order total (inc. VAT) is:{" "}
            <strong>
              {formatCurrency(priceMismatchMode.data.serverPrice)}
            </strong>
          </p>
          <p>Do you wish to accept this new order total?</p>
          <div className="flex justify-end gap-2">
            {priceMismatchDeclineButton}
            {priceMismatchAcceptButton}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DistributionEducationDeliveryMethodAlert = ({
  eligibleFulfillmentTypes,
  selectedFulfillmentType,
  supplier,
  basketOrder,
  buyerPostcode,
}: {
  eligibleFulfillmentTypes: CheckoutSelectedFulfillmentType[];
  selectedFulfillmentType: CheckoutSelectedFulfillmentType;
  supplier: SupplierGetResponseType;
  basketOrder: ReturnType<typeof useBasketContext>["0"]["order"];
  buyerPostcode: string;
}) => {
  const canDisplayAlert =
    eligibleFulfillmentTypes.includes("sellarDistribution") &&
    (selectedFulfillmentType === "courier" ||
      selectedFulfillmentType === "haulier");

  const { nextDeliveryDate: deliveryPlannedAt } =
    useDistributionDeliveryEstimate({
      supplierId: supplier.id,
      buyerPostcode,
      productIds:
        basketOrder?.OrderItems?.map((orderItem) => orderItem.ProductId) ?? [],
    });

  const openDistributionOrderListQuery = useOpenDistributionOrderList(
    {
      query: deliveryPlannedAt
        ? [
            {
              type: "deliveryPlannedAt",
              modifier: "EQUALS",
              value: deliveryPlannedAt,
            } as const,
          ]
        : [],
      limit: 1,
    },
    {
      enabled: Boolean(deliveryPlannedAt) && canDisplayAlert,
    },
  );

  if (!canDisplayAlert) {
    return null;
  }

  const openDistributionOrders =
    openDistributionOrderListQuery.data?.pages.flat() ?? [];

  const hasOpenDistributionOrders = openDistributionOrders.length > 0;

  if (!hasOpenDistributionOrders) {
    return null;
  }

  return (
    <div className="item-center mt-2 flex gap-2 rounded-lg border border-blue-300 bg-blue-100 p-3 text-sm text-blue-800">
      <div>
        <Icon icon="INFORMATION" size={0.8} />
      </div>
      <p className="font-normal">
        Switch to <span className="font-semibold">Delivered by Sellar</span> to
        get this order delivered in your existing delivery slot scheduled for{" "}
        <span className="font-semibold">
          {dayjs(deliveryPlannedAt).format("DD/MM/YY")}
        </span>
      </p>
    </div>
  );
};
